import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { IFrameTraceRenderer } from '@/components/IFrameTraceRenderer';
import { Text } from '@/design-system';
import {
  IFrameTraceRenderingConfig,
  useIFrameTraceRendering,
} from '@/hooks/useIFrameTraceRendering';
import { RunInputSchema, RunOutputSchema, RunSchema } from '@/types/schema';

import { RunDisplaySchema } from '../utils/types';
import { Skeleton } from './InputOutputLoadingSkeleton';
import { RunInput } from './RunInput';
import { RunOutput } from './RunOutput';
import { getPlainPayload } from './utils/RunDetailsIO.utils';

interface RunDetailsPlainProps {
  run: RunDisplaySchema;
  prevRun?: RunSchema | undefined;
  variant: [
    'plain',
    {
      input:
        | { type: 'key-heuristic' }
        | { type: 'key'; value: string }
        | { type: 'key'; value: string[] };
      output:
        | { type: 'key-heuristic' }
        | { type: 'key'; value: string }
        | { type: 'key'; value: string[] };
    }
  ];
  inputs?: RunInputSchema;
  outputs?: RunOutputSchema;
  forceRawRender?: 'raw' | 'rendered';
  onEditInputs?: (inputs: RunSchema['inputs']) => void;
  onEditOutputs?: (outputs: RunSchema['outputs']) => void;
  inputsRef?: React.RefObject<HTMLDivElement>;
  outputsRef?: React.RefObject<HTMLDivElement>;
  inputsLoading?: boolean;
  outputsLoading?: boolean;
  errorsLoading?: boolean;
}

export function PlainSingleInput({
  inputsLoading,
  inputsRef,
  onEditInputs,

  run,
  prevRun,
  plainInput,
}: {
  inputsLoading: boolean | undefined;
  inputsRef: React.RefObject<HTMLDivElement> | undefined;
  onEditInputs: ((inputs: RunInputSchema | undefined) => void) | undefined;
  run: RunDisplaySchema;
  prevRun: RunSchema | undefined;
  plainInput: ReturnType<typeof getPlainPayload>;
}) {
  return (
    <div
      className="mr-auto flex max-w-full flex-col items-start gap-4 pr-12 has-[[data-virtuoso-scroller]]:w-full"
      ref={inputsRef}
      data-testid="run-details-input"
    >
      {inputsLoading ? (
        <Skeleton />
      ) : (
        <RunInput
          runType={run.run_type}
          attachments={run.inputs_s3_urls}
          inputs={plainInput}
          foldDistractions={{ prevRun }}
          onEditInputs={onEditInputs}
        />
      )}
    </div>
  );
}

export function PlainSingleOutput({
  outputsRef,
  error,
  outputsLoading,
  errorsLoading,
  currIFrameConfig,
  outputs,
  forceRawRender,
  plainOutput,
  onEditOutputs,
  run,
  inputs,
}: {
  outputsRef: React.RefObject<HTMLDivElement> | undefined;
  error: string | undefined;
  outputsLoading: boolean | undefined;
  errorsLoading: boolean | undefined;
  currIFrameConfig: IFrameTraceRenderingConfig | undefined;
  outputs: RunOutputSchema | undefined;
  forceRawRender: 'raw' | 'rendered' | undefined;
  plainOutput: ReturnType<typeof getPlainPayload>;
  onEditOutputs: ((outputs: RunSchema['outputs']) => void) | undefined;
  run: RunDisplaySchema;
  inputs: RunInputSchema | undefined;
}) {
  return (
    <div
      className="ml-auto flex max-w-full flex-col items-end gap-4 pl-12"
      ref={outputsRef}
    >
      <ExpandableErrorAlert error={error} />

      {outputsLoading || errorsLoading ? (
        <Skeleton />
      ) : currIFrameConfig?.enabled && outputs && forceRawRender !== 'raw' ? (
        <IFrameTraceRenderer
          config={currIFrameConfig}
          payload={outputs}
          type="output"
          key={`${run.id}-output`}
        />
      ) : (
        plainOutput && (
          <RunOutput
            attachments={run.outputs_s3_urls}
            outputs={plainOutput}
            foldDistractions={{ inputs: inputs }}
            onEditOutputs={onEditOutputs}
            runType={run.run_type}
          />
        )
      )}
    </div>
  );
}

export function RunDetailsPlain({
  run,
  prevRun,
  variant,
  inputs,
  outputs,
  forceRawRender,
  onEditInputs,
  onEditOutputs,
  inputsRef,
  outputsRef,
  inputsLoading,
  outputsLoading,
  errorsLoading,
}: RunDetailsPlainProps) {
  const { error } = run;
  const { currIFrameConfig } = useIFrameTraceRendering(
    run.reference_dataset_id
  );

  const plainInput = getPlainPayload(inputs, variant[1].input);

  const plainOutput = getPlainPayload(outputs, variant[1].output);

  return (
    <>
      <div className="flex flex-col gap-2">
        <Text variant="h4">Inputs</Text>{' '}
        <PlainSingleInput
          inputsLoading={inputsLoading}
          inputsRef={inputsRef}
          onEditInputs={onEditInputs}
          run={run}
          prevRun={prevRun}
          plainInput={plainInput}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Text className="text-right" variant="h4">
          Outputs
        </Text>{' '}
        <PlainSingleOutput
          outputsRef={outputsRef}
          error={error}
          outputsLoading={outputsLoading}
          errorsLoading={errorsLoading}
          currIFrameConfig={currIFrameConfig}
          outputs={outputs}
          forceRawRender={forceRawRender}
          plainOutput={plainOutput}
          onEditOutputs={onEditOutputs}
          run={run}
          inputs={inputs}
        />
      </div>
    </>
  );
}
